import { useState, useEffect, useRef, useCallback } from 'react';

type Phase = 'idle' | 'typing' | 'deploying' | 'complete';
type TabId = 'template' | 'import' | 'ai';

interface Tab {
  id: TabId;
  icon: string;
  label: string;
  badge?: string;
}

const TABS: Tab[] = [
  { id: 'template', icon: 'widgets', label: 'Pick a Template' },
  { id: 'import', icon: 'link', label: 'Import a Site' },
  { id: 'ai', icon: 'smart_toy', label: 'Build with AI', badge: 'NEW' },
];

const MINI_TEMPLATES = [
  { name: 'Modern SaaS Landing', frameworks: ['html', 'tailwind'] },
  { name: 'Developer Portfolio', frameworks: ['react', 'vite'] },
  { name: 'E-Commerce Starter', frameworks: ['react', 'tailwind'] },
];

const FRAMEWORK_COLORS: Record<string, string> = {
  html: 'bg-red-500/20 text-red-400',
  tailwind: 'bg-sky-500/20 text-sky-400',
  react: 'bg-cyan-500/20 text-cyan-400',
  vite: 'bg-purple-500/20 text-purple-400',
};

const IMPORT_URL = 'https://my-bakery.com';
const AI_USER_MSG = 'Build me a restaurant site with a menu and online ordering';
const AI_REPLY_MSG = 'On it — generating your site with React + LiteDB...';

const DEPLOY_LABELS: Record<TabId, string> = {
  template: 'Deploying...',
  import: 'Importing...',
  ai: 'Generating...',
};

const AUTO_CYCLE_MS = 6000;

export default function DeployDemo() {
  const [activeTab, setActiveTab] = useState<TabId>('template');
  const [phase, setPhase] = useState<Phase>('idle');
  const [typedLen, setTypedLen] = useState(0);
  const [aiStep, setAiStep] = useState(0); // 0=none, 1=user bubble, 2=ai bubble
  const [selectedTemplate, setSelectedTemplate] = useState(-1);
  const [progress, setProgress] = useState(0);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const sectionRef = useRef<HTMLElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cycleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phaseRef = useRef<Phase>('idle');

  // Keep ref in sync
  phaseRef.current = phase;

  // --- IntersectionObserver to start on scroll ---
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasBeenVisible) {
          setHasBeenVisible(true);
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasBeenVisible]);

  // --- Reset state when tab changes ---
  const resetState = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setPhase('idle');
    setTypedLen(0);
    setAiStep(0);
    setSelectedTemplate(-1);
    setProgress(0);
  }, []);

  // --- Run animation sequence for current tab ---
  const runAnimation = useCallback(
    (tab: TabId) => {
      resetState();

      // Small delay to let idle render
      timerRef.current = setTimeout(() => {
        if (tab === 'template') {
          // Step 1: highlight template card
          setPhase('typing'); // reuse typing phase as "selecting"
          setSelectedTemplate(0);

          // Step 2: start deploy
          timerRef.current = setTimeout(() => {
            setPhase('deploying');
            setProgress(0);
            requestAnimationFrame(() => setProgress(100));

            // Step 3: complete
            timerRef.current = setTimeout(() => {
              setPhase('complete');
            }, 2200);
          }, 1200);
        } else if (tab === 'import') {
          // Step 1: type URL
          setPhase('typing');
          const url = IMPORT_URL;
          let i = 0;
          const typeInterval = setInterval(() => {
            i++;
            setTypedLen(i);
            if (i >= url.length) {
              clearInterval(typeInterval);
              // Step 2: deploy
              timerRef.current = setTimeout(() => {
                setPhase('deploying');
                setProgress(0);
                requestAnimationFrame(() => setProgress(100));

                timerRef.current = setTimeout(() => {
                  setPhase('complete');
                }, 2200);
              }, 600);
            }
          }, 60);
        } else if (tab === 'ai') {
          // Step 1: show user bubble
          setPhase('typing');
          setAiStep(1);

          // Step 2: show AI reply
          timerRef.current = setTimeout(() => {
            setAiStep(2);

            // Step 3: deploy
            timerRef.current = setTimeout(() => {
              setPhase('deploying');
              setProgress(0);
              requestAnimationFrame(() => setProgress(100));

              timerRef.current = setTimeout(() => {
                setPhase('complete');
              }, 2200);
            }, 1200);
          }, 1400);
        }
      }, 300);
    },
    [resetState],
  );

  // --- Trigger animation when visible or tab changes ---
  useEffect(() => {
    if (hasBeenVisible) {
      runAnimation(activeTab);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [activeTab, hasBeenVisible, runAnimation]);

  // --- Auto-cycle tabs ---
  useEffect(() => {
    if (!hasBeenVisible || isPaused) {
      if (cycleRef.current) clearTimeout(cycleRef.current);
      return;
    }

    cycleRef.current = setTimeout(() => {
      setActiveTab((prev) => {
        const idx = TABS.findIndex((t) => t.id === prev);
        return TABS[(idx + 1) % TABS.length].id;
      });
    }, AUTO_CYCLE_MS);

    return () => {
      if (cycleRef.current) clearTimeout(cycleRef.current);
    };
  }, [activeTab, hasBeenVisible, isPaused, phase]);

  const handleTabClick = (id: TabId) => {
    setIsPaused(true);
    setActiveTab(id);
    // Resume auto-cycle after one full cycle
    setTimeout(() => setIsPaused(false), AUTO_CYCLE_MS);
  };

  // --- Tab content renderers ---
  const renderTemplateDemo = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {MINI_TEMPLATES.map((tpl, i) => (
          <div
            key={tpl.name}
            className={`relative rounded-lg border p-3 transition-all duration-500 ${
              selectedTemplate === i
                ? 'border-orange-500 bg-orange-500/10 ring-1 ring-orange-500/40'
                : 'border-zinc-700/50 bg-zinc-800/30'
            }`}
          >
            {/* Mini preview placeholder */}
            <div className="aspect-[4/3] rounded bg-zinc-700/30 mb-2 flex items-center justify-center">
              <span className="material-icons text-zinc-600 text-xl">web</span>
            </div>
            <p className="text-xs font-medium text-zinc-300 truncate">{tpl.name}</p>
            <div className="flex gap-1 mt-1.5">
              {tpl.frameworks.map((fw) => (
                <span
                  key={fw}
                  className={`px-1 py-0.5 rounded text-[8px] font-medium uppercase ${FRAMEWORK_COLORS[fw] || 'bg-zinc-700/50 text-zinc-400'}`}
                >
                  {fw}
                </span>
              ))}
            </div>
            {selectedTemplate === i && (
              <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                <span className="material-icons text-white text-xs">check</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedTemplate >= 0 && phase === 'typing' && (
        <div className="flex justify-center animate-fade-in">
          <button className="px-5 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-semibold rounded-lg animate-pulse-glow">
            <span className="material-icons text-sm mr-1 align-middle">rocket_launch</span>
            Deploy
          </button>
        </div>
      )}
    </div>
  );

  const renderImportDemo = () => (
    <div className="space-y-4">
      <div className="relative">
        <div className="flex items-center bg-zinc-800/60 border border-zinc-700/50 rounded-lg overflow-hidden">
          <span className="material-icons text-zinc-500 text-lg pl-3">link</span>
          <div className="flex-1 px-3 py-2.5 font-mono text-sm text-zinc-300 min-h-[40px] flex items-center">
            {phase !== 'idle' ? (
              <span>
                {IMPORT_URL.slice(0, typedLen)}
                <span className="deploy-cursor" />
              </span>
            ) : (
              <span className="text-zinc-600">Enter your site URL...</span>
            )}
          </div>
          <button
            className={`px-4 py-2.5 text-sm font-medium transition-all ${
              typedLen >= IMPORT_URL.length && phase === 'typing'
                ? 'bg-orange-500 text-white'
                : 'bg-zinc-700 text-zinc-400'
            }`}
          >
            Import
          </button>
        </div>
      </div>
      {phase === 'typing' && typedLen >= IMPORT_URL.length && (
        <p className="text-xs text-zinc-500 text-center animate-fade-in">
          Your site, now on WiserWebCloud
        </p>
      )}
    </div>
  );

  const renderAiDemo = () => (
    <div className="space-y-3 min-h-[140px]">
      {/* User bubble */}
      <div
        className={`flex justify-end transition-all duration-500 ${
          aiStep >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}
      >
        <div className="max-w-[80%] bg-orange-500/15 border border-orange-500/30 rounded-2xl rounded-br-md px-4 py-2.5">
          <p className="text-sm text-orange-200">{AI_USER_MSG}</p>
        </div>
      </div>

      {/* AI bubble */}
      <div
        className={`flex justify-start transition-all duration-500 ${
          aiStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}
      >
        <div className="max-w-[80%] bg-zinc-800/60 border border-zinc-700/50 rounded-2xl rounded-bl-md px-4 py-2.5">
          <p className="text-sm text-zinc-300">
            <span className="material-icons text-orange-400 text-sm align-middle mr-1">smart_toy</span>
            {AI_REPLY_MSG}
          </p>
        </div>
      </div>

      {/* Footer */}
      {aiStep >= 2 && (
        <p className="text-xs text-zinc-600 text-center animate-fade-in">
          Powered by{' '}
          <a href="https://wiserweb.ai" className="text-orange-400/60 hover:text-orange-400 transition">
            wiserweb.ai
          </a>
        </p>
      )}
    </div>
  );

  // --- Deploy progress bar ---
  const renderProgressBar = () => {
    if (phase !== 'deploying') return null;
    return (
      <div className="mt-4 animate-fade-in">
        <div className="flex items-center gap-2 mb-2">
          <span className="material-icons text-orange-400 text-sm deploy-spin">sync</span>
          <span className="text-xs text-zinc-400">{DEPLOY_LABELS[activeTab]}</span>
        </div>
        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all ease-out"
            style={{
              width: `${progress}%`,
              transitionDuration: '2000ms',
            }}
          />
        </div>
      </div>
    );
  };

  // --- Result panel ---
  const renderResult = () => {
    if (phase !== 'complete') return null;
    return (
      <div className="mt-5 animate-fade-in-up">
        {/* Browser chrome */}
        <div className="bg-zinc-800/80 border border-zinc-700/60 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-700/40 bg-zinc-900/50">
            <div className="flex gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
            </div>
            <div className="flex-1 flex items-center gap-1.5 bg-zinc-800/80 rounded-md px-2.5 py-1 ml-2">
              <span className="material-icons text-green-400 text-xs">lock</span>
              <span className="text-xs text-zinc-400 font-mono">fresh-bakery.wiserwebcloud.com</span>
            </div>
          </div>
          <div className="p-4 flex items-center justify-center">
            <div className="flex flex-wrap justify-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-xs text-emerald-400">
                <span className="material-icons text-xs">bolt</span>
                Deployed in 8s
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-500/10 border border-sky-500/30 rounded-full text-xs text-sky-400">
                <span className="material-icons text-xs">lock</span>
                Free SSL
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-full text-xs text-purple-400">
                <span className="material-icons text-xs">dns</span>
                Custom domain ready
              </span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-4 text-center">
          <a
            href="https://dashboard.wiserwebcloud.com/signup"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-semibold rounded-lg hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
          >
            Start Free
            <span className="material-icons text-sm">arrow_forward</span>
          </a>
        </div>
      </div>
    );
  };

  return (
    <section
      ref={sectionRef}
      className="py-24 px-6 bg-zinc-900/30 relative overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-orange-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            From Idea to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
              Live Site
            </span>
          </h2>
          <p className="text-lg text-zinc-400">Pick your starting point — we handle the rest.</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-3 mb-8">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-orange-500/15 border border-orange-500/40 text-orange-400 shadow-glow'
                  : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:text-zinc-300 hover:border-zinc-600'
              }`}
            >
              <span className="material-icons text-lg">{tab.icon}</span>
              {tab.label}
              {tab.badge && (
                <span className="px-1.5 py-0.5 bg-orange-500 text-white text-[9px] font-bold rounded uppercase">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Demo panel */}
        <div className="bg-zinc-900/60 border border-zinc-800/80 rounded-2xl p-6 sm:p-8 backdrop-blur-sm">
          {activeTab === 'template' && renderTemplateDemo()}
          {activeTab === 'import' && renderImportDemo()}
          {activeTab === 'ai' && renderAiDemo()}

          {renderProgressBar()}
          {renderResult()}
        </div>

        {/* LiteDB Upsell Strip */}
        <div className="mt-10 bg-zinc-900/60 border border-orange-500/20 rounded-2xl p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded uppercase">
                  NEW
                </span>
                <h3 className="text-lg font-semibold text-white">Make any site dynamic with LiteDB</h3>
              </div>
              <p className="text-sm text-zinc-400 mb-4">
                Add a real database to your static site — offline-first, auto-syncing, ~50KB.
              </p>
              <a
                href="/features"
                className="inline-flex items-center gap-1.5 text-sm text-orange-400 hover:text-orange-300 font-medium transition"
              >
                Explore LiteDB
                <span className="material-icons text-sm">arrow_forward</span>
              </a>
            </div>
            <div className="w-full lg:w-auto lg:min-w-[340px]">
              <div className="bg-zinc-950/80 border border-zinc-800 rounded-lg p-4 font-mono text-sm">
                <div className="text-zinc-500">
                  <span className="text-orange-400">const</span>{' '}
                  <span className="text-cyan-400">db</span>{' '}
                  <span className="text-zinc-500">=</span>{' '}
                  <span className="text-orange-400">new</span>{' '}
                  <span className="text-cyan-400">LiteDB</span>
                  <span className="text-zinc-300">{'({'}</span>
                </div>
                <div className="pl-4 text-zinc-300">
                  <span className="text-zinc-400">apiKey</span>
                  <span className="text-zinc-500">:</span>{' '}
                  <span className="text-green-400">"ww_live_..."</span>
                </div>
                <div className="text-zinc-300">{'});'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
